// The first report (week one) on a member's report page: start one, or open one to review. The page picks the report
// type; this is what shows when it's "First report". Unlike the progress report, it runs as a job and is reviewed on its
// own page (/first-report/:id).
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, mockOn } from "./api";
import { fmtDate } from "./logic";
import "./WeekOneReview.css";

const STATUS = { draft: "Draft: needs review", approved: "Approved: writing", ready: "Written" };

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// What a report started now would read, from the service (it picks the days from the member's timeline; nobody picks a date).
function Coverage({ cov, error }) {
  if (error) return <p className="muted" style={{ margin: "0 0 10px" }}>Couldn't check the member's data just now: {error}</p>;
  if (!cov) return <p className="muted" style={{ margin: "0 0 10px" }}>Checking the member's data…</p>;
  if (!cov.ready) return <div className="banner" style={{ margin: "0 0 10px" }}><b>Not ready yet.</b> {cov.why}</div>;
  return (
    <div style={{ margin: "0 0 10px" }}>
      <b>Covers {fmtDate(cov.start)} – {fmtDate(cov.end)}:</b> {plural(cov.calls, "call")}, {plural(cov.sensorDays, "sensor day")},{" "}
      {plural(cov.meals, "meal")} logged.
      {cov.notes.length > 0 && (
        <ul className="muted" style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12.5 }}>
          {cov.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}

// reports: the member's first reports (api().listReports), or null while loading.
export default function FirstReportPanel({ memberId, reports, loadError }) {
  const navigate = useNavigate();
  const [cov, setCov] = useState(null);
  const [covError, setCovError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setCov(null);
    setCovError(null);
    api().coverage(memberId).then((c) => live && setCov(c)).catch((e) => live && setCovError(e.message));
    return () => { live = false; };
  }, [memberId]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const id = await api().startReport(memberId);
      navigate(`/first-report/${id}`);
    } catch (e) {
      setError(e.message);
      api().logError({ memberId, where: "start", status: e.status, message: e.message });
      setStarting(false);
    }
  };

  return (
    <div className="w1 embedded">
      {mockOn() && <p style={{ margin: "0 0 10px" }}><span className="mock">Mock data</span></p>}
      {(error || loadError) && <div className="err" role="alert"><span>{error || loadError}</span>{error && <button onClick={() => setError(null)}>Dismiss</button>}</div>}

      <section className="panel">
        <h2>Start the first report</h2>
        <ul className="muted" style={{ margin: "2px 0 10px", paddingLeft: 18, fontSize: 12.5 }}>
          <li>Start it 1–2 weeks after the member's first care-team call, once they have sensor data or logged meals.</li>
          <li>It reads every call note, and the member's data up to their last day of data (the latest 21 days at most), going back no further than two weeks before the first call, so a trial sensor counts.</li>
          <li>It's dated today, and its seven-day check reads a week from today.</li>
        </ul>
        <Coverage cov={cov} error={covError} />
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 12.5 }}>
          It writes a content plan for you to review (about a minute). Nothing goes to the member until you approve it.
        </p>
        <div className="row">
          <button className="primary" disabled={starting || (cov && !cov.ready)} onClick={start}>{starting ? "Starting…" : "Start the report"}</button>
        </div>
      </section>

      <section className="panel">
        <h2>First reports</h2>
        {reports === null ? (
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        ) : reports.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>None yet.</p>
        ) : (
          <div className="tablewrap">
            <table className="list">
              <tbody>
                <tr><th>Report date</th><th>Status</th><th>Last step</th><th>Approved by</th><th></th></tr>
                {reports.map((r) => (
                  <tr key={r._id}>
                    <td>{fmtDate(r.reportDate)}</td>
                    <td>{STATUS[r.status] || r.status}</td>
                    <td className="muted">{r.job ? `${r.job.kind}: ${r.job.state === "running" ? r.job.step : r.job.state}` : ""}</td>
                    <td>{r.approval ? `${r.approval.name}${r.approval.purpose === "member" ? "" : " (other)"}` : ""}</td>
                    <td><Link to={`/first-report/${r._id}`}>Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
